/**
 * PitchEngine — reference-grade offline pitch shifting for Timbrica
 * Wraps signalsmith-stretch (MIT) AudioWorklet into a clean offline-render API.
 *
 * Usage:
 *   const out = await PitchEngine.shift(audioBuffer, {
 *     semitones: -3,
 *     preserveFormants: true,          // natural voice character on big shifts
 *     formantSemitones: 0,             // optional independent formant shift
 *     onProgress: pct => {}            // 0..1
 *   });
 *   // out is a new AudioBuffer with same sample rate, same length, shifted pitch
 */
(function(){
'use strict';

// Load order (RU-aware, same pattern as lamejs loader):
//   1) VPS static                    — X-Served-From: vps-direct, works from Russia without origin
//   2) VPS /cdn/jsdelivr proxy       — VPS fetches from jsdelivr on cache miss
//   3) direct jsdelivr               — last resort (may be blocked in some jurisdictions)
// Records which source succeeded via telemetry so we can see RU network issues vs global.
var SCRIPT_SOURCES = [
    // ?v=1.3.2-p1 — patched 4× `??=` to `X=X??Y` for Chrome <85 compat (see top of vendored file).
    // The CDN-proxy and direct-jsdelivr sources still serve UPSTREAM unpatched 1.3.2 → on Chrome <85
    // they parse-fail and fall through to legacy FFmpeg path. Only vps-static carries the patch.
    // Bumped query string on every pitch-engine change so cached old bundles get refreshed.
    { url: '/vendor/signalsmith-stretch/SignalsmithStretch.js?v=1.3.2-p1-c2',          tag: 'vps-static' },
    { url: '/cdn/jsdelivr/npm/signalsmith-stretch@1.3.2/SignalsmithStretch.js',        tag: 'vps-cdn-proxy' },
    { url: 'https://cdn.jsdelivr.net/npm/signalsmith-stretch@1.3.2/SignalsmithStretch.js', tag: 'direct-jsdelivr' }
];
var _loadPromise = null;
var _loadedSource = null;

function tryLoad(src){
    return new Promise(function(resolve, reject){
        var s = document.createElement('script');
        s.src = src.url;
        s.async = true;
        s.onload = function(){
            if (typeof window.SignalsmithStretch === 'function') resolve(src.tag);
            else reject(new Error('loaded but SignalsmithStretch missing: ' + src.tag));
        };
        s.onerror = function(){ reject(new Error('fetch failed: ' + src.tag)); };
        document.head.appendChild(s);
    });
}

function reportTelemetry(type, payload){
    try { if (typeof window.__ae === 'function') window.__ae(type, payload); } catch(_){}
}

// slug is optional — tells telemetry which tool triggered the load. Several tools now use
// PitchEngine (pitch, time-stretch, future: ringtone, speed), so we thread the caller's
// slug through instead of hardcoding 'pitch'.
function loadLib(slug){
    if (_loadPromise) return _loadPromise;
    _loadPromise = (async function(){
        if (typeof window.SignalsmithStretch === 'function') return 'already-loaded';
        var errors = [];
        for (var i = 0; i < SCRIPT_SOURCES.length; i++) {
            try {
                _loadedSource = await tryLoad(SCRIPT_SOURCES[i]);
                if (i > 0) reportTelemetry('feature_use', { slug: slug || 'audio-pipeline', action: 'engine_load_fallback', src: _loadedSource, skipped: errors });
                return _loadedSource;
            }
            catch(e) {
                var msg = SCRIPT_SOURCES[i].tag + ':' + (e.message || 'err');
                console.warn('[pitch-engine] source failed:', msg);
                errors.push(msg);
            }
        }
        reportTelemetry('feature_use', { slug: slug || 'audio-pipeline', action: 'engine_load_all_failed', errors: errors.join('|').substring(0, 200) });
        throw new Error('All sources failed: ' + errors.join(' | '));
    })();
    return _loadPromise;
}

function emptyBuffer(ch, length, sr){
    // Routed through window.__safeAudioBuffer (timbrica-core.js) so the same
    // Android-Chrome / Mobile-Safari length-cap telemetry covers the pitch
    // engine's intermediate buffers — Sentry sweep 2026-05-06 made this the
    // canonical entry point for AudioBuffer allocation.
    if (typeof window.__safeAudioBuffer === 'function') {
        return window.__safeAudioBuffer({numberOfChannels: ch, length: length, sampleRate: sr});
    }
    // timbrica-core.js failed to load — fall back to the original ladder.
    if (typeof AudioBuffer === 'function') {
        try { return new AudioBuffer({numberOfChannels: ch, length: length, sampleRate: sr}); }
        catch(_) {}
    }
    var ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(ch, length, sr);
    return ctx.createBuffer(ch, length, sr);
}

async function shift(audioBuffer, opts){
    opts = opts || {};
    var semitones = Number(opts.semitones) || 0;
    var preserveFormants = !!opts.preserveFormants;
    var formantSemitones = Number(opts.formantSemitones) || 0;
    // rate: time stretch factor. 0.5 = output twice as long (slower), 2 = twice as fast.
    // Independent of `semitones` — you can slow down without changing pitch.
    var rate = Number(opts.rate) || 1;
    if (rate <= 0) rate = 1;
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;

    var isNoOp = Math.abs(semitones) < 0.001
        && !preserveFormants
        && formantSemitones === 0
        && Math.abs(rate - 1) < 0.001;
    if (isNoOp) {
        var copy = emptyBuffer(audioBuffer.numberOfChannels, audioBuffer.length, audioBuffer.sampleRate);
        for (var c = 0; c < audioBuffer.numberOfChannels; c++) {
            copy.getChannelData(c).set(audioBuffer.getChannelData(c));
        }
        return copy;
    }

    await loadLib(opts.slug);

    var ch = audioBuffer.numberOfChannels;
    var sr = audioBuffer.sampleRate;
    var inLen = audioBuffer.length;
    var inDur = inLen / sr;

    // Output duration depends on rate: rate=2 means output is half input duration.
    var outDur = inDur / rate;
    var outLen = Math.round(outDur * sr);

    // Safety pad for internal latency — 1 second is plenty even at block=120ms.
    // The pad is in OUTPUT time (after rate change), so it doesn't scale with rate.
    var pad = 1.0;
    var renderLen = Math.ceil((outDur + pad) * sr);

    var OfflineCtor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var offCtx = new OfflineCtor({numberOfChannels: ch, length: renderLen, sampleRate: sr});

    var stretch = await window.SignalsmithStretch(offCtx, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [ch]
    });

    // Single-call addBuffers with TRANSFERABLE hand-off.
    //
    // Plain `addBuffers([Float32Array, …])` would ship the bytes via
    // structured cloning — the postMessage engine COPIES the buffer,
    // doubling the heap peak briefly. With 7 prod events of
    // "Failed to execute 'postMessage': Data cannot be cloned, out of memory"
    // on 2 GB devices (2026-04-26 → 2026-05-02), the cheap fix is to
    // transfer the ArrayBuffer instead of cloning it.
    //
    // Why NOT chunk into multiple addBuffers calls (regression 2026-05-02 → 2026-05-04):
    //   The vendored SignalsmithStretch.js worklet's offline-feed
    //   `process()` has a multi-buffer arithmetic bug: when the requested
    //   read crosses a chunk boundary, iter 2 attempts a `.set(longer, off)`
    //   that throws RangeError("offset is out of bounds"). The worklet
    //   silently fills zeros from that block onward → the rest of the
    //   render is silent. Our 75 s battery test missed this because it
    //   only ffprobed codec/sr/ch metadata, not audio content (RMS).
    //   Bug was visible for every file > 30 s = effectively all music.
    //
    // .slice() copies into a fresh ArrayBuffer that we own and can transfer
    // safely without detaching the AudioBuffer's internal storage.
    //
    // For files larger than this single copy can hold (≳ deviceMemory/4 ×
    // 0.5), AudioPipeline.stretch's memory guard fires upstream and never
    // reaches us — see audio-pipeline.js stretchIsSafe().
    var buffers = [];
    var transfers = [];
    for (var c2 = 0; c2 < ch; c2++) {
        var copy = new Float32Array(audioBuffer.getChannelData(c2));
        buffers.push(copy);
        transfers.push(copy.buffer);
    }
    // Auto-generated method signature: argCount is 1 (sampleBuffers); the
    // 2nd arg is treated as the transferable list. See SignalsmithStretch.js
    // line ~430 (`if (args.length > argCount) transfer = args.pop()`).
    await stretch.addBuffers(buffers, transfers);

    var latencySec = 0;
    try { latencySec = await stretch.latency(); } catch(_) { latencySec = 0.1; }
    try { window.__ae && window.__ae('feature_use', { slug: 'audio-pipeline',
        action: 'pitch_engine_latency', latSec: +Number(latencySec).toFixed(4), rate: rate }); } catch(_) {}

    await stretch.schedule({
        output: 0,
        input: 0,
        rate: rate,
        semitones: semitones,
        formantCompensation: preserveFormants,
        formantSemitones: formantSemitones,
        active: true
    });

    stretch.connect(offCtx.destination);
    stretch.start(0);

    if (onProgress) {
        var progressTimer = setInterval(function(){
            var pct = Math.min(0.95, (stretch.inputTime || 0) / inDur);
            onProgress(pct);
        }, 100);
        offCtx.oncomplete = function(){ clearInterval(progressTimer); onProgress(1); };
    }

    var rendered = await offCtx.startRendering();

    /* Head-skip is ZERO here, and that is not an oversight.
       This path hosts Signalsmith as an AudioWorklet inside an
       OfflineAudioContext: addBuffers() hands over the whole input up front and
       start(0) renders it, so the output already sits on input time. The
       algorithmic pre-roll that the C-API Worker build MUST discard
       (signalsmith-worker.js) never appears in this scheme — but this code
       skipped `latency()` anyway, which does not remove a pre-roll, it DELETES
       the first 0.12 s of the user's audio.

       MEASURED on prod 2026-09-01, clicks at 0.5 s and 2.0 s, error in samples:
         semi  rate | skip=latency()      | skip=0
          -4    1   | -5201, -5226        |  +91, +66
          +5    1   | -5196, -5227        |  +96, +65
          -4    2   | -5300, -5242        |   -8, +50
           0   0.5  | -5285, -5253        |   +7, +39
       ~5200 samples = 118 ms of content gone, on every run, at every setting;
       with skip=0 the residual is 7-96 samples, i.e. the smear a phase vocoder
       puts on a click. Chosen across three different rates precisely so this is
       an identified rule and not a constant fitted to one case.

       `latencySec` is still read and reported: if a future library build DOES
       introduce a pre-roll here, the number is the thing that will show it. */
    var skip = 0;
    var out = emptyBuffer(ch, outLen, sr);
    for (var c3 = 0; c3 < ch; c3++) {
        var src = rendered.getChannelData(c3);
        var dst = out.getChannelData(c3);
        var srcEnd = Math.min(src.length, skip + outLen);
        var count = srcEnd - skip;
        if (count > 0) dst.set(src.subarray(skip, srcEnd), 0);
    }

    await ceilOutput(out, opts.slug, rate, semitones);
    return out;
}

/* The same output ceiling as the worker build (signalsmith-worker.js,
   applyCeiling): the vocoder regrows peaks by 3–5 dB on a loud file, and a
   16-bit export clips them. In place on the buffer's own channels; the two
   modules are fetched only when the output actually crossed full scale. */
function peakOver(buf){
    for (var c = 0; c < buf.numberOfChannels; c++) {
        var d = buf.getChannelData(c);
        for (var i = 0; i < d.length; i++) if (d[i] > 0.999 || d[i] < -0.999) return true;
    }
    return false;
}
async function ceilOutput(buf, slug, rate, semitones){
    if (!peakOver(buf)) return;
    try {
        if (!window.ExportCeiling && typeof window.__loadScriptsOnce === 'function' && typeof window.__jsSrc === 'function') {
            await window.__loadScriptsOnce([window.__jsSrc('/js/mastering-limiter.js'), window.__jsSrc('/js/export-ceiling.js')], 'export-ceiling');
        }
        if (!window.ExportCeiling) return;
        var chans = [];
        for (var c = 0; c < buf.numberOfChannels; c++) chans.push(buf.getChannelData(c));
        var r = await window.ExportCeiling.limitChannelsAsync(chans, buf.sampleRate);
        if (r.engaged && r.out !== chans) {
            for (var c2 = 0; c2 < chans.length; c2++) chans[c2].set(r.out[c2]);
        }
        window.__ae && window.__ae('feature_use', { slug: slug || window.__TOOL_SLUG || 'unknown', f: 'stretch_ceiling',
            host: 'worklet', over_db: Math.round((r.overDb || 0) * 10) / 10, gr_db: Math.round((r.grDb || 0) * 10) / 10,
            rate: Math.round(rate * 1000) / 1000, semi: Math.round(semitones * 10) / 10 });
    } catch (_) { /* a clipped file beats no file; the export path clamps as before */ }
}

window.PitchEngine = {
    shift: shift,
    preload: loadLib,
    version: '1.0.0',
    engine: 'signalsmith-stretch@1.3.2',
    loadedFrom: function(){ return _loadedSource; }
};

})();
